import { CardString, Value, Hand, Card } from 'pokersolver'
import { ActionHint } from '../ActionEvaluator'
import { Suits, Values } from '../Deck'

export enum FACE_CARD_VALUES {
  J = 'J',
  Q = 'Q',
  K = 'K',
  A = 'A'
}

export const FACE_CARDS = [FACE_CARD_VALUES.J, FACE_CARD_VALUES.Q, FACE_CARD_VALUES.K, FACE_CARD_VALUES.A]
export const SPECIAL_SUITED_CONNECTORS_VALUES: [Value, Value][] = [
  ['9', 'T'],
  ['T', 'J'],
  ['J', 'Q'],
  ['Q', 'K'],
  ['K', 'A'],
  ['A', '2']
]

export type BotAction = {
  action: ActionHint
  amount: number
}

const BotAvatars = {
  purple: '#5C2DA8',
  pink: '#AB037C',
  green: '#128942',
  blue: '#0271B7',
  orange: '#D96800',
  red: '#C41313',
  crimson: '#FF7F50',
  teal: '#5B6196',
  lightblue: '#02A0C2',
  gray: '#343434'
}

// TODO: why are this been use instead of BotPlayerKinds.ts > botKinds
export const BOT_KINDS = ['BOT_PP'] as const

export const BOT_NAMES = [
  'TanyaCMing',
  'Samantha Bricker',
  'Carol Schulz',
  'Georgia KOO',
  'AnteAnnie54',
  'AllInAmy99',
  'Lucia Greene',
  'Zoe Reed99',
  'Penelope Baker',
  'Stella Writes',
  'Harper Rodriguez',
  'Isa Jones',
  'Kansas Caterina',
  'Ellie Bronco',
  'Victoria Titans',
  'Lucy Lulu',
  'Stella Giant',
  'Brookie Charger',
  'Bebe WM',
  'MandyB',
  'VeronicaH',
  'Lanie',
  'Sharon V',
  'Greta',
  'Marcie',
  'Finny',
  'Ursula',
  'Maria L',
  'Evelyn Smith',
  'Rachel M',
  'Malorie D',
  'JillyBean',
  'Babs',
  'Kara Sims',
  'Beth K',
  'Nikki Luck',
  'Paula Milner',
  'Claire C',
  'AlwaysLuckyTay',
  'Freya ',
  'Miss Athena',
  'Joan of Arc',
  'JayLo',
  'Katniss E',
  'Athena',
  'Ms.Maisel',
  'EllaMella',
  'Lilly KS',
  'Mia Mia',
  'Zoey Lloyd',
  'Ella Knows',
  'Gracely',
  'IVYGEE',
  'Maya Price',
  'Nora Bee',
  'Jade',
  'MuseMia',
  'Ivy Belle',
  'Sparkle',
  'Luna',
  'Hailee K.',
  'Ms.Potter',
  'Kansas Queen',
  'CocoCharm',
  'VickyC',
  'Anne Grace',
  'Nicky M.',
  'Carmen',
  'Lina St.',
  'Emiko Zee',
  'Zahra Lei',
  'Banana Mama',
  'Orangina',
  'Sunny Side Up',
  'Evelyn Play',
  'Evie Que',
  'Queen Riley',
  'bLakeGame',
  'Lakers Queen',
  'Daisy Plays Poker',
  'Ava Garde',
  'ElleFitz',
  'Alex Plays',
  'Queen Aces',
  'Jazz Wizard',
  'Dani Dee',
  'Nina Q',
  'Boomer B',
  'Taro',
  'RC Queen',
  'ABBY'
]
export const THREE_CARD_BOT_NAMES = ['Jenny J', 'Taylor S']

export const randomIntFromInterval = (min: number, max: number) => {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min)
}

/**
 *
 * @param cards array of cards
 * @returns an array of the cards values
 * @example
 * getCardsValues(['8d', '2h']) // ['8', '2']
 * getCardsValues(['Jd', '5h']) // ['J', '5']
 * getCardsValues(['Ad', '3h']) // ['A', '3']
 */
export const getCardsValues = (cards: CardString[]) => {
  return cards.map((card) => card[0] as Value)
}

/**
 *
 * @param cards array of cards
 * @returns an array of the cards suits
 * @example
 * getCarsdSuits(['8d', '2h']) // ['d', 'h']
 * getCarsdSuits(['Jd', '5c']) // ['d', 'c']
 * getCarsdSuits(['Ah', '3s']) // ['h', 's']
 */
export const getCardsSuits = (cards: CardString[]) => {
  return cards.map((card) => card.slice(-1))
}

/**
 * @param cards array of cards
 * @returns true if the cards have a pair
 * @example
 * hasAPair(['8d', '8h']) // true
 * hasAPair(['2d', '2s']) // true
 * hasAPair(['Qd', 'Qc']) // true
 * hasAPair(['8d', 'Td']) // false
 */
export const hasAPair = (cards: CardString[]): boolean => {
  // valid cards format are ['8d', 'Td'], ['2d', 'Jd'], ['Qd', 'Ad'] see Deck.ts
  const cardValues = getCardsValues(cards)
  const cardValuesSet = new Set(cardValues)
  return cardValues.length !== cardValuesSet.size
}

/**
 * @param cards array of cards
 * @returns true if the cards have a face cards
 * @example
 * hasFaceCards(['Jd', 'Qh']) // true
 * hasFaceCards(['Td', 'Js']) // true
 * hasFaceCards(['Qd', 'Kd']) // true
 * hasFaceCards(['8d', 'Td']) // false
 * */
export const hasFaceCards = (cards: CardString[]): boolean => {
  const cardValues = getCardsValues(cards)
  return cardValues.every((value) => FACE_CARDS.includes(value as FACE_CARD_VALUES))
}

/**
 * @param cards array of cards
 * @returns true if the cards have a suited ace
 * @example
 * hasAceSuited(['Ad', '5d']) // true
 * hasAceSuited(['Ad', '5h']) // false
 *  */
export const hasAceSuited = (cards: CardString[]): boolean => {
  const cardValues = getCardsValues(cards)
  const cardSuits = getCardsSuits(cards)
  return cardValues.includes(FACE_CARD_VALUES.A) && cardSuits.every((suit) => suit === cardSuits[0])
}

export const sortValues = (values: string[]): string[] => {
  return values.sort((a, b) => a.localeCompare(b))
}

/**
 *
 * @returns an array of the special suited connectors values sorted
 */
export const getSortedSpecialSuitedConnectorsValues = () => {
  const sortedSpecialSuitedConnectors = SPECIAL_SUITED_CONNECTORS_VALUES.map((value) => sortValues(value))
  return sortedSpecialSuitedConnectors
}

/**
 * @param cards array of cards
 * @returns true if the cards have a suited connectors
 * @example
 * hasSuitedConnectors(['9d', 'Td']) // true
 * hasSuitedConnectors(['2d', '3d']) // true
 * hasSuitedConnectors(['Qd', 'Kd']) // true
 * hasSuitedConnectors(['8d', 'Td']) // false
 */
export const hasSuitedConnectors = (cards: CardString[]): boolean => {
  const cardValues = getCardsValues(cards)
  const cardSuits = getCardsSuits(cards)
  const cardSuitsSet = new Set(cardSuits)

  // if there are 2 different suits, it's not a suited connector
  if (cardSuitsSet.size !== 1) return false

  const cardValuesArraySorted = sortValues(cardValues)
  const cardValuesArraySortedString = cardValuesArraySorted.join('')
  const specialSuitedConnectorsSorted = getSortedSpecialSuitedConnectorsValues()
  const specialSuitedConnectorsValues = specialSuitedConnectorsSorted.map((value) => value.join(''))
  const valuesAreNextToEachOther = Math.abs(parseInt(cardValues[0]) - parseInt(cardValues[1])) === 1
  return specialSuitedConnectorsValues.includes(cardValuesArraySortedString) || valuesAreNextToEachOther
}

/**
 * @param cards array of cards
 * @returns true if the cards have a flush draw
 * @example
 * hasFlushDraw(['9d', 'Td', 'Ad', '2d', '5s']) // true
 * hasFlushDraw(['9s', 'Th', 'As', '2s', '5s']) // true
 * hasFlushDraw(['Td', 'Qd', 'Ad', '2s', '5d']) // true
 * hasFlushDraw(['8d', 'Td', 'Ad', '3s', '4h']) // false
 */
export const hasFlushDraw = (cards: CardString[]): boolean => {
  const cardSuits = getCardsSuits(cards)

  return Suits.some((suit) => cardSuits.filter((cardSuit) => cardSuit === suit).length === 4)
}

/**
 * @param cards array of cards
 * @returns true if the cards have a flush draw
 * @example
 * hasStraightDraw(['3d', '4s', '5h', '6d']) // true
 * hasStraightDraw(['2d', '4s', '5h', '6d']) // true
 * hasStraightDraw(['6d', '7d', '8d', 'Td']) // true
 * hasStraightDraw(['Ts','8s', 'Ah', '3h'] // false
 * hasStraightDraw(['8d', 'Td', 'As', '3s']) // false
 */
export const hasStraightDraw = (cards: CardString[]): boolean => {
  let canMakeStraight = false
  Values.forEach((value) => {
    Suits.forEach((suit) => {
      const card = new Card(`${value}${suit}`)
      const newCards = [...cards, card.toString()]
      try {
        const solvedHand = Hand.solve(newCards)
        if (solvedHand.name === 'Straight') {
          canMakeStraight = true
        }
      } catch (e) {
        /* empty */
      }
    })
  })

  return canMakeStraight
}

export const hasHighCard = (cards: CardString[]): boolean => {
  try {
    const solvedHand = Hand.solve(cards)
    return solvedHand.name === 'High Card'
  } catch (e) {
    return false
  }
}

export const hasPairOfCard = (cards: CardString[], ofValue: Value): boolean => {
  try {
    const solvedHand = Hand.solve(cards)
    const isPair = solvedHand.name === 'Pair'
    if (!isPair) {
      return false
    }
    const cardValues = getCardsValues(cards)

    return isPair && cardValues.includes(ofValue)
  } catch (e) {
    return false
  }
}

export const hasValueOfCards = (cards: CardString[], ofValue: Value[]): boolean => {
  try {
    const cardValues = getCardsValues(cards)
    return cardValues.every((c) => ofValue.includes(c))
  } catch (e) {
    return false
  }
}
